import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { CheckSquare, CalendarClock, AlertTriangle, CalendarDays, ArrowUpRight } from "lucide-react";
import { PostponeTaskDialog } from "@/components/PostponeTaskDialog";

const todayStr = () => new Date().toISOString().slice(0, 10);
const weekEndStr = () => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [filterAction, setFilterAction] = useState("all");
  const [postponeTask, setPostponeTask] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    try { await api.patch(`/tasks/${id}`, { done: true }); toast.success("Taak afgerond"); load(); }
    catch { toast.error("Bijwerken mislukt"); }
  };

  const actionTypes = Array.from(new Set(tasks.map((t) => t.action_type).filter(Boolean))).sort();
  const filtered = filterAction === "all" ? tasks : tasks.filter((t) => (t.action_type || "") === filterAction);
  const today = todayStr();
  const weekEnd = weekEndStr();
  const overdue = filtered.filter((t) => t.followup_date < today);
  const dueToday = filtered.filter((t) => t.followup_date === today);
  const week = filtered.filter((t) => t.followup_date > today && t.followup_date <= weekEnd);

  const goTo = (t) => { if (t.tenant_id) navigate(`/app/huurders/${t.tenant_id}`); else if (t.client_id) navigate(`/app/opdrachtgevers/${t.client_id}`); };
  const NEXT_INCASSO = { aanmaning14: "venster", venster: "venster" };
  const escalate = (t) => { const nt = NEXT_INCASSO[t.incasso_type] || "venster"; const from = t.letter_id ? `&from=${t.letter_id}` : ""; navigate(`/app/brievengenerator?incasso=1&tenant=${t.tenant_id}&type=${nt}${from}`); };

  const Row = ({ t }) => (
    <div className="flex items-start justify-between gap-2 p-3 rounded-lg border hover:bg-slate-50" data-testid={`task-row-${t.id}`}>
      <div className="flex items-start gap-3 cursor-pointer min-w-0" onClick={() => goTo(t)}>
        <Checkbox onCheckedChange={() => complete(t.id)} data-testid={`task-complete-${t.id}`} onClick={(e) => e.stopPropagation()} />
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800">{t.tenant_name || t.client_name || "—"} <Badge variant="secondary" className="ml-1 capitalize">{t.type}</Badge>{t.action_type && <Badge className="ml-1 bg-indigo-100 text-indigo-700 hover:bg-indigo-100">{t.action_type}</Badge>}</div>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{t.outcome}</p>
          <div className="text-xs text-slate-400 mt-0.5">Opvolgen: {fmtDate(t.followup_date)}</div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
        {t.action_type === "Incassobrief opvolgen" && t.tenant_id && (
          <Button variant="outline" size="sm" title="Escaleren naar volgende incassostap" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={() => escalate(t)} data-testid={`task-escalate-${t.id}`}><ArrowUpRight className="h-3.5 w-3.5 mr-1" /> Escaleren</Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setPostponeTask(t)} data-testid={`task-postpone-${t.id}`}><CalendarClock className="h-3.5 w-3.5 mr-1" /> Uitstellen</Button>
      </div>
    </div>
  );

  const Section = ({ title, icon: Icon, items, tone, tid }) => (
    <Card className="p-5" data-testid={tid}>
      <h3 className={`font-heading font-semibold mb-3 flex items-center gap-2 ${tone}`}><Icon className="h-4 w-4" /> {title} <Badge variant="secondary">{items.length}</Badge></h3>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-400">Geen taken.</p>}
        {items.map((t) => <Row key={t.id} t={t} />)}
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#0F1E3D]">Taken</h1>
          <p className="text-slate-500">Opvolgacties uit gespreksnotities. Vink af zodra afgehandeld.</p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="tasks-action-filter">
          <button onClick={() => setFilterAction("all")} data-testid="tasks-filter-all"
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filterAction === "all" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>Alle acties</button>
          {actionTypes.map((a) => (
            <button key={a} onClick={() => setFilterAction(a)} data-testid={`tasks-filter-${a}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filterAction === a ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}>{a}</button>
          ))}
        </div>
      </div>
      <Section title="Te laat" icon={AlertTriangle} items={overdue} tone="text-red-600" tid="tasks-overdue" />
      <Section title="Vandaag" icon={CalendarClock} items={dueToday} tone="text-amber-600" tid="tasks-today" />
      <Section title="Aankomend deze week" icon={CalendarDays} items={week} tone="text-blue-700" tid="tasks-week" />
      {postponeTask && <PostponeTaskDialog task={postponeTask} onClose={() => setPostponeTask(null)} onDone={() => { setPostponeTask(null); load(); }} />}
    </div>
  );
}
